import React, { useState, useEffect, useMemo, useRef } from "react";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Table,
  Button,
  message,
  Spin,
  notification,
  Segmented,
  Tooltip,
  Modal,
  Checkbox,
  Radio,
  Space,
  Collapse,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FunctionOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useAutoSave } from "../../../hooks/useAutoSave";
import { useItemLibrary, buildItemLibraryFromBoqSnapshot } from "../../../hooks/useItemLibrary";
import { useBoqLatestVersion, useBoqSelectedVersion } from "../../../hooks/useBillOfQuantities";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useGetRetainingWallsBuildUp } from "../../../hooks/useRetainingWallsBuildUp";
import { useGetGeoToberWallBuildUp } from "../../../hooks/useWallSectionBuildUp";
import { mergePersistedWallBuildUp } from "../../../utils/wallsBuildUpSave";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";
import MainLayout from "../../layout/MainLayout";
import {
  generatePlotMatrixCategoryColumns,
  generatePlotMatrixCategoryRows,
  computeSoilBoQTotals,
  computeCutFillFieldBillSummaryData,
  calculateRowTotal,
  INCLUDED_SUB_STAGES,
  DEFAULT_CELL_CALC_STRATEGY,
  buildEvaluateRateMap,
} from "../../../utils/plotMatrixCategoryGenerator";
import type { CellCalcStrategy, BoqStepTargets, CellBreakdown } from "../../../utils/plotMatrixCategoryGenerator";
import { getFormulaModalContent } from "../../../utils/plotMatrixFormulaDefinitions";
import { applyDynamicBillSummaryToPlotMatrixRows } from "../../../utils/dynamicBillSummaryPlotMatrix";
import { exportPlotMatrixToExcel } from "../../../utils/groundworksExcelExport";
import {
  calculateFoundationsGrandTotal,
  calculateCutFillGrandTotal,
  calculateDrainageGrandTotal,
  calculatePlotDrivesGrandTotal,
  calculatePlotServiceGrandTotal,
  calculatePlotPavingsGrandTotal,
  calculateScaffoldGrandTotal,
  calculateGardenGrandTotal,
  calculateRetScreenWallsGrandTotal,
  calculateGEOToberWallGrandTotal,
} from "../../../utils/summaryCalculations";

const { Title } = Typography;

/**
 * Dev-mode cell-calculation strategy options. Each option is rendered in a
 * Radio.Group with an InfoCircleOutlined icon that opens a Tooltip explaining
 * exactly how cells, column totals and row totals are computed for that
 * strategy. The strategy is passed through to `generatePlotMatrixCategoryRows`
 * via the `cellCalcStrategy` parameter — picking a different option re-runs
 * the matrix with the new per-cell formula.
 *
 * Adding a new strategy: append an entry here and add the matching case in
 * `applyCellCalcStrategy` (via the `compute*CellAmount` dispatch) in
 * `plotMatrixCategoryGenerator.ts`. The Radio.Group renders entries from
 * this array, so no UI plumbing changes are needed.
 */
const CELL_CALC_STRATEGY_OPTIONS: ReadonlyArray<{
  value: CellCalcStrategy;
  label: string;
  shortLabel: string;
  info: string;
}> = [
  {
    value: 'proportional',
    label: 'Option A — Proportional Bill Summary share',
    shortLabel: 'Option A',
    info:
      'Cell  = (rowQty / totalQty) × billSummaryAmount,\n' +
      '         where billSummaryAmount = round(|Math.round(totalQty) × rate|, 2).\n' +
      'Column total = Σ cells in the column.\n' +
      'Row total    = Σ cells in the row.\n' +
      'Grand total  = Σ column totals = Σ row totals.\n\n' +
      'Each row\'s cell is its proportional share of the field\'s rounded-qty BoQ amount. `rate` is the 2dp-rounded item-library rate — the same value the estimator sees in the build-up rate cells and the Bill Summary rate column. Current production behavior.',
  },
  {
    value: 'rawRowQty',
    label: 'Option B — Raw row quantity × rate',
    shortLabel: 'Option B',
    info:
      'Cell  = rowQty × rate (signed, no qty rounding).\n' +
      'Column total = Σ rowQty × rate.\n' +
      'Row total    = Σ rowQty × rate.\n' +
      'Grand total  = Σ column totals = Σ row totals.\n\n' +
      'Literal per-row math: cell = qty × visible rate. Example: qty 3, rate 1.35 → cell = 4.05. `rate` is the 2dp-rounded item-library rate, so the cell value matches what the estimator can verify by hand against the Bill Summary rate column.',
  },
  // Option C temporarily hidden — uncomment to restore.
  // {
  //   value: 'roundedRowQty',
  //   label: 'Option C — Math.round(rowQty) × rate',
  //   shortLabel: 'Option C',
  //   info:
  //     'Cell  = Math.round(rowQty) × rate (signed).\n' +
  //     'Column total = Σ Math.round(rowQty) × rate.\n' +
  //     'Row total    = Σ Math.round(rowQty) × rate.\n' +
  //     'Grand total  = Σ column totals = Σ row totals.\n\n' +
  //     'Each row\'s qty is rounded individually before multiplying by the 2dp-rounded rate. Example: qty 3.4, rate 1.35 → cell = round(3.4) × 1.35 = 3 × 1.35 = 4.05.',
  // },
];

/**
 * Display a plot number with the appropriate prefix ("P-" for plot rows, "G-"
 * for garage rows) without double-prefixing values that already carry one.
 * Mirrors the storage-side normalisation done by Foundations / Scaffold so the
 * Plot Matrix shows P-1 / G-1 even for legacy rows that still hold "1".
 */
const formatPlotNoWithPrefix = (
  plotNo: string | undefined,
  rowType?: string,
): string => {
  const raw = (plotNo ?? '').toString();
  if (!raw) return raw;
  if (/^[PG]-/i.test(raw)) return raw;
  return `${rowType === 'garage' ? 'G' : 'P'}-${raw}`;
};

interface PlotMatrixRow extends Record<string, unknown> {
  key?: string;
  plotNo?: string;
  _isSeparator?: boolean;
  rowType?: string;
}

interface PlotMatrixProps {
  embedded?: boolean; // When true, renders without MainLayout wrapper
  /**
   * Externally-controlled cell-calculation strategy (dev mode only). When
   * provided, the parent owns the state — typically `GroundworksReview` which
   * renders the switcher in its BoQ / Plot Matrix tab bar so the control sits
   * next to the tabs instead of inside this component's toolbar. When both
   * `cellCalcStrategy` and `onCellCalcStrategyChange` are provided, the
   * internal toolbar switcher is suppressed (parent has its own UI). When
   * neither is provided, this component falls back to its own internal state
   * + toolbar switcher (used by the standalone full-page Plot Matrix view).
   */
  cellCalcStrategy?: CellCalcStrategy;
  onCellCalcStrategyChange?: (strategy: CellCalcStrategy) => void;
}

const PlotMatrix: React.FC<PlotMatrixProps> = ({
  embedded = false,
  cellCalcStrategy: cellCalcStrategyProp,
  onCellCalcStrategyChange,
}) => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state } = useTender();
  const { settings } = useGlobalSettings();

  // Historical version support — mirrors SumAnalysis.tsx / GroundworksReview.tsx.
  // Without this, Plot Matrix always priced off the LIVE item library even when
  // viewing an old BOQ version, so its totals (e.g. Foundations' RC28/35 Concrete
  // to Piled foundation) diverged from Sum Analysis/Review's totals as soon as any
  // rate changed after that version was saved.
  const [isHistoricVersion, setIsHistoricVersion] = useState<boolean>(
    () => localStorage.getItem("isHistoricalVersion") === "true"
  );
  useEffect(() => {
    const check = () => setIsHistoricVersion(localStorage.getItem("isHistoricalVersion") === "true");
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, []);
  const { data: latestVersionData } = useBoqLatestVersion(state.projectName || undefined, { enabled: !isHistoricVersion, oppNo: state.opportunityNo });
  const { data: selectedVersionData } = useBoqSelectedVersion(state.projectName || undefined, { enabled: isHistoricVersion, oppNo: state.opportunityNo });
  const versionData = isHistoricVersion ? selectedVersionData : latestVersionData;
  const { data: liveItemLibraryData, isLoading: isLoadingItemLibrary } =
    useItemLibrary(state.region || "", undefined, { enabled: !isHistoricVersion || !versionData }, undefined, state.opportunityNo);
  const historicalItemLibraryData = useMemo(
    () => (isHistoricVersion ? buildItemLibraryFromBoqSnapshot(versionData) : null),
    [isHistoricVersion, versionData],
  );
  const itemLibraryData = isHistoricVersion ? (historicalItemLibraryData ?? liveItemLibraryData) : liveItemLibraryData;

  const formulaInspectorRateMap = useMemo(() => buildEvaluateRateMap(itemLibraryData), [itemLibraryData]);
  useAutoSave();
  const navigate = useNavigate();

  // View mode toggle: "plot" = existing detailed view, "stage" = valuation stage grouped view
  const [viewMode, setViewMode] = useState<"plot" | "stage">("plot");

  // Cell-calculation strategy (dev-mode only). Outside dev mode the strategy
  // is locked to `DEFAULT_CELL_CALC_STRATEGY` ('proportional') — same as the
  // current production behavior. See `CellCalcStrategy` in
  // `plotMatrixCategoryGenerator.ts` for the three options.
  //
  // Two control modes:
  //  1. **Parent-controlled** — `cellCalcStrategyProp` + `onCellCalcStrategyChange`
  //     are passed in (used by the embedded view in `GroundworksReview` so the
  //     switcher can render next to the BoQ / Plot Matrix tabs). The internal
  //     state below is ignored; the internal toolbar switcher is suppressed.
  //  2. **Self-controlled** — neither prop is passed (used by the standalone
  //     full-page Plot Matrix view). The internal `useState` owns the value
  //     and the toolbar switcher renders it.
  // Either way, `cellCalcStrategy` is the resolved value passed to
  // `generatePlotMatrixCategoryRows`.
  const [internalCellCalcStrategy, setInternalCellCalcStrategy] =
    useState<CellCalcStrategy>(DEFAULT_CELL_CALC_STRATEGY);
  const cellCalcStrategy = cellCalcStrategyProp ?? internalCellCalcStrategy;
  const setCellCalcStrategy = onCellCalcStrategyChange ?? setInternalCellCalcStrategy;
  const isStrategyControlledByParent =
    cellCalcStrategyProp !== undefined && onCellCalcStrategyChange !== undefined;

  // Modal state for the info icon next to each cell-calc strategy radio in
  // the standalone toolbar. Renders the formula explanation as a readable
  // Modal instead of a clipped Tooltip.
  const [cellCalcInfoIdx, setCellCalcInfoIdx] = useState<number | null>(null);
  const scrollPositionRef = useRef<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });

  // Generate dynamic columns and rows from groundworks data (category-based)
  const [dynamicColumns, setDynamicColumns] = useState<any[]>([]);
  const [plotMatrixData, setPlotMatrixData] = useState<any[]>([]);
  // isReady: false until the first matrix computation completes (prevents showing empty table briefly)
  const [isReady, setIsReady] = useState(false);
  // Drives the `loading` spinner on both "Export to Excel" buttons below,
  // from click until `exportPlotMatrixToExcel` (workbook build + browser
  // download trigger) actually resolves.
  const [isExportingToExcel, setIsExportingToExcel] = useState(false);

  // Dev-mode formula inspector modal
  const [formulaModal, setFormulaModal] = useState<{
    visible: boolean;
    categoryName: string;
    fieldNames: string[];
  }>({ visible: false, categoryName: '', fieldNames: [] });

  // Dev-mode per-cell breakdown modal
  const [cellBreakdownModal, setCellBreakdownModal] = useState<{
    visible: boolean;
    breakdown: CellBreakdown;
    cellTotal: number;
    columnName: string;
    plotNo: string;
  } | null>(null);

  const ratesMapping = null;

  // ─── Markup toggles (prelims / OH&P) ──────────────────────────────────────
  // Sum Analysis writes localStorage keys when its checkboxes are toggled;
  // Plot Matrix mirrors them so the matrix's uplifted cells stay in lockstep
  // with the Sum Analysis tender total. `getCheckboxState` matches the helper
  // in Garden / CutFills / etc.: honour the saved value if present, otherwise
  // default to "checked when the percentage > 0" (so a fresh tender with a
  // non-zero default shows the markup without the user having to toggle it
  // on first). Discount was removed — Plot Matrix no longer applies discount
  // and no longer renders DISCOUNT / TENDER TOTAL footer rows.
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      try {
        return JSON.parse(saved);
      } catch {
        return fallbackPercent > 0;
      }
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = settings.groundworks?.preliminaries ?? 0;
  const ohpPercent = settings.groundworks?.overheadAndProfit ?? 0;

  const [includePreliminaries, setIncludePreliminaries] = useState(() =>
    getCheckboxState('groundworks_includePreliminariesDisplay', preliminariesPercent),
  );
  const [includeOhp, setIncludeOhp] = useState(() =>
    getCheckboxState('groundworks_includeOhpDisplay', ohpPercent),
  );

  // Poll localStorage so toggles made on Sum Analysis propagate here without
  // a refresh. 500 ms matches the existing pattern Sum Analysis uses for
  // the `isHistoricalVersion` flag.
  useEffect(() => {
    const check = () => {
      const nextPrelim = getCheckboxState('groundworks_includePreliminariesDisplay', preliminariesPercent);
      const nextOhp = getCheckboxState('groundworks_includeOhpDisplay', ohpPercent);
      setIncludePreliminaries((prev) => (prev === nextPrelim ? prev : nextPrelim));
      setIncludeOhp((prev) => (prev === nextOhp ? prev : nextOhp));
    };
    check();
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, [preliminariesPercent, ohpPercent]);

  // Renders the Sum Analysis-style markup checkboxes (Preliminaries, OH&P) as
  // disabled visual indicators matching Sum Analysis's read-only display.
  // Hidden entirely when both percentages are zero so the header doesn't
  // carry empty noise.
  const renderMarkupCheckboxes = () => {
    if (preliminariesPercent <= 0 && ohpPercent <= 0) {
      return null;
    }
    return (
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        {ohpPercent > 0 && (
          <Checkbox checked={includeOhp} disabled>
            OH&P ({ohpPercent}%)
          </Checkbox>
        )}
        {preliminariesPercent > 0 && (
          <Checkbox checked={includePreliminaries} disabled>
            Preliminaries ({preliminariesPercent}%)
          </Checkbox>
        )}
      </div>
    );
  };

  // Cell-calculation strategy switcher. Renders a Radio.Group with one radio
  // per `CELL_CALC_STRATEGY_OPTIONS` entry; each label is followed by an
  // InfoCircleOutlined icon that opens a Modal describing the per-cell /
  // per-column / per-row formula for that strategy. Returns `null` when the
  // parent component controls the strategy via props (e.g. `GroundworksReview`
  // renders its own copy next to the BoQ / Plot Matrix tabs, so the internal
  // toolbar control would be a duplicate). Previously gated by `showDevMode`
  // — now shown to all users.
  const renderCellCalcStrategySwitcher = () => {
    if (isStrategyControlledByParent) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 8px',
          border: '1px dashed #d1d5db',
          borderRadius: 4,
          background: '#fafafa',
        }}
      >
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
          Cell calc:
        </span>
        <Radio.Group
          value={cellCalcStrategy}
          onChange={(e) => setCellCalcStrategy(e.target.value as CellCalcStrategy)}
          size="small"
        >
          <Space size={4}>
            {CELL_CALC_STRATEGY_OPTIONS.map((opt, idx) => (
              <Radio key={opt.value} value={opt.value}>
                <span style={{ fontSize: 12 }}>{opt.shortLabel}</span>
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: '#1677ff', fontSize: 12, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCellCalcInfoIdx(idx);
                  }}
                />
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );
  };

  // Per-cell uplift factor — sequential: OH&P applied first, then Prelims.
  // (1 + ohp%) × (1 + prelim%) matches all bill summaries and Sum Analysis.
  const upliftFactor = useMemo(() => {
    const ohp = includeOhp ? ohpPercent : 0;
    const prelim = includePreliminaries ? preliminariesPercent : 0;
    return (1 + ohp / 100) * (1 + prelim / 100);
  }, [includePreliminaries, includeOhp, preliminariesPercent, ohpPercent]);

  // Pre-load wall build-up data from API — avoids dependency on visiting the walls tabs
  const tenderName = state.projectName || "";
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    // version may be stored as "GWKS-3"/"RS-3" (set by /version-history) — must
    // extract the plain numeric part first, otherwise these fetches send the
    // wrong (or no) version to the API, so a historical version's retaining
    // walls/geo-tober walls specs come back empty and its Plot Matrix totals
    // (Retaining Walls, Screen Walls) diverge from Sum Analysis/Review.
    return raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 0) : 0;
  })()).current;
  const { data: retWallsBuildUp } = useGetRetainingWallsBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName,
  });
  const { data: geoToberWallBuildUp } = useGetGeoToberWallBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName,
  });

  // Merge persisted API wall build-up dimensions into groundworks data for
  // plot matrix calculation. Shared helper so other callers of
  // `generatePlotMatrixCategoryRows` (e.g. `GroundworksReview.tsx`) can apply
  // the same merge — otherwise their Plot Matrix Retaining Walls Foundation
  // column silently misses the wall amounts.
  const groundworksWithWallDimensions = useMemo(() => {
    return mergePersistedWallBuildUp(
      state.groundworks as Record<string, unknown> | undefined,
      retWallsBuildUp,
      geoToberWallBuildUp,
    ) as typeof state.groundworks;
  }, [state.groundworks, retWallsBuildUp, geoToberWallBuildUp]);


  // Generate dynamic plot matrix from groundworks step data (category-based)
  useEffect(() => {
    if (isLoadingItemLibrary || !groundworksWithWallDimensions) {
      // Still fetching — mark as not ready so the spinner shows
      setIsReady(false);
      return;
    }

    // Mark as not ready to show spinner while the heavy computation runs,
    // then defer the computation one tick so React can paint the spinner first.
    setIsReady(false);
    const timer = setTimeout(() => {
      // The eight fields previously hosted by the synthetic "Uncategorized"
      // column now live in the named "Foundation Exc & Conc - Abnormals"
      // sub-column (see `PLOT_MATRIX_FOUNDATION_EXC_CONC_CATEGORIES`),
      // so no synthetic injection is needed — `generatePlotMatrixCategoryColumns`
      // returns the full column set on its own. `appendUncategorizedColumn`
      // and the multi-step Uncategorized dispatch in
      // `generatePlotMatrixCategoryRows` are dormant; kept for any future
      // cross-step orphan field that needs a temporary home.
      const columns = generatePlotMatrixCategoryColumns(groundworksWithWallDimensions);
      setDynamicColumns(columns);

      // Compute Soil Management BoQ totals (Section 3.1 and 3.2) for Plot Matrix distribution
      const soilBoQTotals = computeSoilBoQTotals(groundworksWithWallDimensions, itemLibraryData);

      // Compute per-section BoQ targets for reconciliation inside
      // `applyPlotMatrixUplift`.  Each section's `calculate*GrandTotal()` value
      // (the same source Sum Analysis uses) is multiplied by `upliftFactor` so
      // any delta between the Plot Matrix section total and the BoQ section total
      // is distributed only within that section's columns — not spread across the
      // whole matrix.  When the item library hasn't loaded, pass `undefined` and
      // per-column Hamilton alone runs.
      const gwBoq = groundworksWithWallDimensions as any;

      // `fillToMakeUp6F2` lives in CUT_FILL_CATEGORIES (so calculateCutFillGrandTotal
      // includes it) but is displayed in the Plot Matrix under the Plot Drives
      // "Private Drives to Binder - Sub Base" column (cross-sourced from cutFillsTable).
      // calculatePlotDrivesGrandTotal does NOT include it. Without correction the
      // reconciliation finds a large positive delta for Cut & Fills and a large
      // negative delta for Plot Drives, causing it to inflate Cut/Fill cells and
      // deflate Drives cells by the 6F2 amount. Adjust the targets so each section
      // target matches exactly what the Plot Matrix columns actually display.
      const cutFillFieldData = itemLibraryData
        ? computeCutFillFieldBillSummaryData(gwBoq, itemLibraryData)
        : new Map<string, { totalQty: number; billSummaryAmount: number; rate: number; resourceCode: string }>();
      const fill6F2Amount = cutFillFieldData.get('fillToMakeUp6F2')?.billSummaryAmount ?? 0;

      // Pass upliftFactor as markupFactor so targets use the same rounding path
      // as Sum Analysis: markup baked into rate at 2dp, then qty × roundedRate.
      // Post-multiplying base totals gives a different result due to rounding
      // (round(rate × mk, 2) ≠ round(rate, 2) × mk), causing a visible mismatch.
      const mk = upliftFactor;
      const fill6F2MarkedUp = fill6F2Amount * mk;
      const boqStepTargets: BoqStepTargets | undefined = itemLibraryData
        ? new Map<string, number>([
            // Subtract 6F2 amount: it's in the BoQ Cut/Fill total but shown in Drives PM column.
            ['Cut & Fills',      calculateCutFillGrandTotal(gwBoq, null, itemLibraryData, mk) - fill6F2MarkedUp],
            ['Foundations',      calculateFoundationsGrandTotal(gwBoq, null, itemLibraryData, mk)],
            ['Drainage',         calculateDrainageGrandTotal(gwBoq, null, itemLibraryData, mk)],
            // Add 6F2 amount: it's not in the BoQ Drives total but IS in the Drives PM column.
            ['Plot Drives',      calculatePlotDrivesGrandTotal(gwBoq, null, itemLibraryData, mk) + fill6F2MarkedUp],
            ['Plot Service',     calculatePlotServiceGrandTotal(gwBoq, null, itemLibraryData, mk)],
            ['Plot Pavings',     calculatePlotPavingsGrandTotal(gwBoq, null, itemLibraryData, mk)],
            ['Scaffold',         calculateScaffoldGrandTotal(gwBoq, null, itemLibraryData, mk)],
            ['Garden',           calculateGardenGrandTotal(gwBoq, null, itemLibraryData, mk)],
            ['Ret & Screen Walls',
              calculateRetScreenWallsGrandTotal(gwBoq, null, itemLibraryData, retWallsBuildUp?.specifications, mk) +
              calculateGEOToberWallGrandTotal(gwBoq, null, itemLibraryData, geoToberWallBuildUp?.specifications, mk)],
          ])
        : undefined;

      // Generate rows with category totals using current rate card and item library.
      // `upliftFactor` is applied inside `generatePlotMatrixCategoryRows` so every
      // cell — Site Strip, Cut/Fill, Foundations, Scaffold, Drainage, Plot Services,
      // Plot Pavings, Garden, Ret & Screen Walls, Plot Drives, etc. — carries the
      // `(1 + prelim% + ohp%)` markup uniformly.
      const rows = generatePlotMatrixCategoryRows(
        groundworksWithWallDimensions,
        columns,
        ratesMapping,
        itemLibraryData,
        soilBoQTotals,
        settings.groundworks.overheadAndProfit ?? 0,
        // Cell-calc strategy is now user-selectable on all builds (default
        // `DEFAULT_CELL_CALC_STRATEGY` = 'proportional', users may switch to
        // 'rawRowQty' via the toolbar radio group; 'roundedRowQty' / Option C
        // is temporarily hidden from the option list).
        cellCalcStrategy,
        upliftFactor,
        undefined,          // boqCanonicalGrandTotal (legacy global path, unused)
        boqStepTargets,     // per-section targets: delta stays within each section
      );
      // Additive-only overlay — no-ops (returns `rows` unchanged) unless at
      // least one Dynamic Bill Summary item has been mapped to a Plot Matrix
      // column. Never alters the generation above.
      const rowsWithDynamicBillSummary = applyDynamicBillSummaryToPlotMatrixRows(
        rows,
        columns,
        groundworksWithWallDimensions?.dynamicBillSummaryTabs ?? [],
        upliftFactor,
        groundworksWithWallDimensions?.cutFillsExtraItems,
        groundworksWithWallDimensions?.foundationsExtraItems,
        groundworksWithWallDimensions?.drainageExtraItems,
        groundworksWithWallDimensions?.plotDrivesExtraItems,
        groundworksWithWallDimensions?.plotServiceExtraItems,
        groundworksWithWallDimensions?.scaffoldExtraItems,
        groundworksWithWallDimensions?.plotPavingsExtraItems,
        groundworksWithWallDimensions?.gardenExtraItems,
        groundworksWithWallDimensions?.retainingWallsExtraItems,
        groundworksWithWallDimensions?.geoWallsExtraItems,
      );
      setPlotMatrixData(rowsWithDynamicBillSummary);
      setIsReady(true);

      // ─── Dev-mode per-section reconciliation diagnostic ─────────────────
      // Prints a side-by-side comparison of (Plot Matrix grand total per
      // step) vs (BoQ Sum Analysis section total) so any remaining gap is
      // pinpointable at a section level. Only fires in dev mode and only
      // logs if both sides are computable (item library + at least one
      // step has data).
      if (showDevMode && itemLibraryData) {
        const gw = groundworksWithWallDimensions as any;
        const ratesMappingArg = null;
        const summary: Array<{ section: string; pmTotal: number; boqTotal: number; diff: number }> = [];
        const pmStepTotals = new Map<string, number>();
        rows.forEach((row) => {
          columns.forEach((col) => {
            const cell = (row as any)[col.dataIndex];
            const val = cell?.total || 0;
            const step = col.stepName;
            pmStepTotals.set(step, (pmStepTotals.get(step) || 0) + val);
          });
        });

        const sectionMap: Array<{ name: string; pmSteps: string[]; boqFn: () => number }> = [
          { name: 'Cut & Fill',                pmSteps: ['Cut & Fills'],                                       boqFn: () => calculateCutFillGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Foundations',               pmSteps: ['Foundations'],                                       boqFn: () => calculateFoundationsGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Drainage',                  pmSteps: ['Drainage'],                                          boqFn: () => calculateDrainageGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Plot Drives',               pmSteps: ['Plot Drives', 'Private Drives Surface', 'Shared Drives Surface'], boqFn: () => calculatePlotDrivesGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Plot Service+Service Conn', pmSteps: ['Plot Service', 'Service Connections'],               boqFn: () => calculatePlotServiceGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Plot Pavings',              pmSteps: ['Plot Pavings'],                                      boqFn: () => calculatePlotPavingsGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Scaffold',                  pmSteps: ['Scaffold'],                                          boqFn: () => calculateScaffoldGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          { name: 'Garden',                    pmSteps: ['Garden'],                                            boqFn: () => calculateGardenGrandTotal(gw, ratesMappingArg, itemLibraryData) },
          // Ret & Screen Walls + Geowall & Tobermore are merged into the
          // Plot Matrix "Ret & Screen Walls" step (Retaining Walls Foundation
          // column wraps both wall families), so compare their combined
          // BoQ section sum.
          { name: 'Walls (Ret+Screen+Geo)',    pmSteps: ['Ret & Screen Walls'],                                boqFn: () => calculateRetScreenWallsGrandTotal(gw, ratesMappingArg, itemLibraryData, retWallsBuildUp?.specifications) + calculateGEOToberWallGrandTotal(gw, ratesMappingArg, itemLibraryData, geoToberWallBuildUp?.specifications) },
        ];

        sectionMap.forEach(({ name, pmSteps, boqFn }) => {
          const pmTotal = pmSteps.reduce((acc, s) => acc + (pmStepTotals.get(s) || 0), 0);
          const boqTotal = boqFn();
          summary.push({ section: name, pmTotal, boqTotal, diff: pmTotal - boqTotal });
        });

        const pmGrand = summary.reduce((s, r) => s + r.pmTotal, 0);
        const boqGrand = summary.reduce((s, r) => s + r.boqTotal, 0);
        // eslint-disable-next-line no-console
        console.table(
          summary.map((r) => ({
            Section: r.section,
            'Plot Matrix': r.pmTotal.toFixed(2),
            'Sum Analysis': r.boqTotal.toFixed(2),
            'PM − SA': r.diff.toFixed(2),
          })).concat([{
            Section: '— GRAND TOTAL —',
            'Plot Matrix': pmGrand.toFixed(2),
            'Sum Analysis': boqGrand.toFixed(2),
            'PM − SA': (pmGrand - boqGrand).toFixed(2),
          }])
        );
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [
    state.groundworks?.cutFillsTable,
    state.groundworks?.foundationsTable,
    state.groundworks?.drainageTable,
    state.groundworks?.drainageTableStandard,
    state.groundworks?.plotDrivesTable,
    state.groundworks?.plotServiceTable,
    state.groundworks?.plotPavingsTable,
    state.groundworks?.scaffoldTable,
    state.groundworks?.gardenTable,
    state.groundworks?.retainingWallsTable,
    state.groundworks?.geoToberWallTable,
    state.groundworks?.wallsBuildUpData,
    state.groundworks?.geoToberWallBuildUpData,
    settings.groundworks.overheadAndProfit,
    (state.groundworks as any)?.topsoilManagement,
    (state.groundworks as any)?.subsoilManagement,
    (state.groundworks as any)?.soilManagementData,
    state.groundworks?.dynamicBillSummaryTabs,
    state.groundworks?.cutFillsExtraItems,
    state.groundworks?.foundationsExtraItems,
    state.groundworks?.drainageExtraItems,
    state.groundworks?.plotDrivesExtraItems,
    state.groundworks?.plotServiceExtraItems,
    state.groundworks?.scaffoldExtraItems,
    state.groundworks?.plotPavingsExtraItems,
    state.groundworks?.gardenExtraItems,
    state.groundworks?.retainingWallsExtraItems,
    state.groundworks?.geoWallsExtraItems,
    showDevMode,
    ratesMapping,
    itemLibraryData,
    isLoadingItemLibrary,
    retWallsBuildUp,
    geoToberWallBuildUp,
    cellCalcStrategy,
    upliftFactor,
  ]);

  // Generate table columns dynamically with grouped headers
  const generateTableColumns = () => {
    // Total number of leaf columns (2 fixed + dynamic + 1 total)
    const totalColSpan = 2 + dynamicColumns.length + 1;

    const baseColumns = [
      {
        title: "Valuation Stage",
        onHeaderCell: () => ({
          style: { textAlign: "center" },
        }),
        children: [
          {
            title: "Plot No",
            dataIndex: "plotNo",
            key: "plotNo",
            width: 110,
            fixed: "left" as const,
            onHeaderCell: () => ({
              style: { textAlign: "center" },
            }),
            onCell: (record: any) => {
              if (record._isSeparator) return { colSpan: totalColSpan };
              return {};
            },
            render: (_: any, record: any) => {
              if (record._isSeparator) {
                return (
                  <strong style={{ fontSize: "13px", color: "#374151", letterSpacing: "0.05em" }}>
                    GARAGES
                  </strong>
                );
              }
              return formatPlotNoWithPrefix(record.plotNo, record.rowType);
            },
          },
          {
            title: "House Type",
            dataIndex: "housetype",
            key: "housetype",
            width: 120,
            fixed: "left" as const,
            onHeaderCell: () => ({
              style: { textAlign: "center" },
            }),
            onCell: (record: any) => {
              if (record._isSeparator) return { colSpan: 0 };
              return {};
            },
          },
        ],
      },
    ];

    // Group columns by main header (Site Strip, Cut/Fill, etc.)
    const groupedColumns: { [key: string]: any[] } = {};

    dynamicColumns.forEach((col) => {
      // Parse category name: "Site Strip - Strip to remove Vegetation" or "Cut/Fill - Cut/Fill to Foundations"
      const parts = col.categoryName.split(" - ");
      const hasSeparator = parts.length > 1;
      const mainHeader = parts[0] || col.categoryName;
      const subHeader = parts[1] || col.categoryName;

      if (!groupedColumns[mainHeader]) {
        groupedColumns[mainHeader] = [];
      }

      groupedColumns[mainHeader].push({
        // Stored so the conversion pass below can detect the trailing
        // "Uncategorized" placeholder (its categoryName has no " - ") and
        // render it flat without nesting. Steps whose categoryName follows
        // the "Main - Sub" pattern — even when Main === Sub (e.g.
        // "Plot Services - Plot Services") — keep the nested header so the
        // sub-column title and dev-mode formula inspector icon stay visible.
        _subHeader: subHeader,
        _hasSeparator: hasSeparator,
        title: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <Tooltip title={subHeader}>
              <div
                style={{
                  fontWeight: 600,
                  color: "#1890ff",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {subHeader}
              </div>
            </Tooltip>
            {showDevMode && (
              <FunctionOutlined
                style={{ color: "#9ca3af", fontSize: 11, cursor: "pointer", flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFormulaModal({ visible: true, categoryName: col.categoryName, fieldNames: col.fieldNames });
                }}
              />
            )}
          </div>
        ),
        dataIndex: col.dataIndex,
        key: col.key,
        width: 150,
        align: "right" as const,
        onHeaderCell: () => ({
          style: { textAlign: "center", overflow: "hidden" },
        }),
        onCell: (record: any) => {
          if (record._isSeparator) return { colSpan: 0 };
          return {};
        },
        render: (_: any, record: any) => {
          if (record._isSeparator) return null;
          const cellData = record[col.dataIndex];
          const total = cellData?.total ?? 0;
          if (INCLUDED_SUB_STAGES.has(col.categoryName) && total === 0) {
            return "Included";
          }
          const formatted = total > 0
            ? `£${total.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "£0.00";
          if (showDevMode && total > 0 && cellData?._breakdown) {
            return (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3 }}>
                <span>{formatted}</span>
                <InfoCircleOutlined
                  style={{ color: "#9ca3af", fontSize: 10, cursor: "pointer", flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCellBreakdownModal({
                      visible: true,
                      breakdown: cellData._breakdown,
                      cellTotal: total,
                      columnName: col.categoryName,
                      plotNo: record.plotNo ?? '',
                    });
                  }}
                />
              </div>
            );
          }
          return formatted;
        },
      });
    });

    // Convert grouped columns to table structure with main headers. The
    // trailing "Uncategorized" placeholder (categoryName has no " - "
    // separator) renders as a flat column so its single label doesn't stack
    // twice in the header. Steps whose categoryName follows the "Main - Sub"
    // pattern always stay nested, even when Main === Sub (e.g. "Plot Services
    // - Plot Services"), so the sub-column header and the dev-mode formula
    // inspector icon remain visible.
    const dynamicGroupedCols = Object.keys(groupedColumns).map((mainHeader) => {
      const groupChildren = groupedColumns[mainHeader];

      if (
        groupChildren.length === 1 &&
        groupChildren[0]._hasSeparator === false
      ) {
        const child = groupChildren[0];
        return {
          ...child,
          title: (
            <Tooltip title={mainHeader}>
              <span
                style={{
                  fontWeight: 700,
                  color: "#1f2937",
                  fontSize: "14px",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                {mainHeader}
              </span>
            </Tooltip>
          ),
        };
      }

      return {
        title: (
          <Tooltip title={mainHeader}>
            <span
              style={{
                fontWeight: 700,
                color: "#1f2937",
                fontSize: "14px",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {mainHeader}
            </span>
          </Tooltip>
        ),
        onHeaderCell: () => ({
          style: { textAlign: "center", overflow: "hidden" },
        }),
        children: groupChildren,
      };
    });

    // Add row total column
    const totalColumn = {
      title: "Total",
      dataIndex: "rowTotal",
      key: "rowTotal",
      width: 130,
      fixed: "right" as const,
      align: "right" as const,
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
      onCell: (record: any) => {
        if (record._isSeparator) return { colSpan: 0 };
        return {};
      },
      render: (_: any, record: any) => {
        if (record._isSeparator) return null;
        const total = calculateRowTotal(record);
        return (
          <strong>
            £
            {total.toLocaleString("en-GB", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </strong>
        );
      },
    };

    return [...baseColumns, ...dynamicGroupedCols, totalColumn];
  };

  const columns = generateTableColumns();

  // `plotMatrixData` is already uplifted at source — `generatePlotMatrixCategoryRows`
  // applies the `(1 + prelim% + ohp%)` factor to every cell `total` before
  // returning, so the display data is just an alias here. (Previously this
  // ran `applyPlotMatrixUplift` as a separate post-processing pass, but moving
  // the uplift inline guarantees every step — not just Site Strip / Cut/Fill —
  // carries the markup uniformly.)
  const displayPlotMatrixData = plotMatrixData;

  // --- Valuation Stage View: collapse sub-columns into one column per category ---
  const { stageColumns, stageData, stageGroupKeys } = useMemo(() => {
    // Build ordered list of main headers (category groups) preserving insertion order
    const groupOrder: string[] = [];
    const groupDataIndexes: { [mainHeader: string]: string[] } = {};

    dynamicColumns.forEach((col) => {
      const lastDashIdx = col.categoryName.lastIndexOf(" - ");
      const mainHeader = lastDashIdx !== -1 ? col.categoryName.slice(0, lastDashIdx) : col.categoryName;
      if (!groupDataIndexes[mainHeader]) {
        groupDataIndexes[mainHeader] = [];
        groupOrder.push(mainHeader);
      }
      groupDataIndexes[mainHeader].push(col.dataIndex);
    });

    // Total leaf columns in stage view: 2 fixed + groupOrder.length + 1 total
    const stageTotalColSpan = 2 + groupOrder.length + 1;

    // Create one column per category group
    const stageCols = [
      {
        title: "Valuation Stage",
        onHeaderCell: () => ({ style: { textAlign: "center" as const } }),
        children: [
          {
            title: "Plot No",
            dataIndex: "plotNo",
            key: "plotNo",
            width: 110,
            fixed: "left" as const,
            onHeaderCell: () => ({ style: { textAlign: "center" as const } }),
            onCell: (record: any) => {
              if (record._isSeparator) return { colSpan: stageTotalColSpan };
              return {};
            },
            render: (_: any, record: any) => {
              if (record._isSeparator) {
                return (
                  <strong style={{ fontSize: "13px", color: "#374151", letterSpacing: "0.05em" }}>
                    GARAGES
                  </strong>
                );
              }
              return formatPlotNoWithPrefix(record.plotNo, record.rowType);
            },
          },
          {
            title: "House Type",
            dataIndex: "housetype",
            key: "housetype",
            width: 120,
            fixed: "left" as const,
            onHeaderCell: () => ({ style: { textAlign: "center" as const } }),
            onCell: (record: any) => {
              if (record._isSeparator) return { colSpan: 0 };
              return {};
            },
          },
        ],
      },
      ...groupOrder.map((header) => ({
        title: (
          <Tooltip title={header}>
            <span
              style={{
                fontWeight: 700,
                color: "#1f2937",
                fontSize: "14px",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {header}
            </span>
          </Tooltip>
        ),
        dataIndex: `stage_${header}`,
        key: `stage_${header}`,
        width: 160,
        align: "right" as const,
        onHeaderCell: () => ({
          style: { textAlign: "center" as const, overflow: "hidden" },
        }),
        onCell: (record: any) => {
          if (record._isSeparator) return { colSpan: 0 };
          return {};
        },
        render: (_: any, record: any) => {
          if (record._isSeparator) return null;
          const val = record[`stage_${header}`] || 0;
          return val > 0
            ? `£${val.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "£0.00";
        },
      })),
      {
        title: "Total",
        dataIndex: "stageRowTotal",
        key: "stageRowTotal",
        width: 130,
        fixed: "right" as const,
        align: "right" as const,
        onHeaderCell: () => ({ style: { textAlign: "center" as const } }),
        onCell: (record: any) => {
          if (record._isSeparator) return { colSpan: 0 };
          return {};
        },
        render: (_: any, record: any) => {
          if (record._isSeparator) return null;
          const total = record.stageRowTotal || 0;
          return (
            <strong>
              £
              {total.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          );
        },
      },
    ];

    // Transform row data: sum sub-columns into stage columns. Source is the
    // uplifted `displayPlotMatrixData` so each stage cell carries the
    // prelims+OH&P uplift (since stage cell = Σ sub-cells, and each sub-cell
    // has been multiplied by `upliftFactor`).
    const sDataRaw = displayPlotMatrixData.map((row) => {
      const newRow: any = {
        key: row.key,
        plotNo: row.plotNo,
        housetype: row.housetype,
        rowType: row.rowType,
      };
      let rowTotal = 0;

      groupOrder.forEach((header) => {
        let sum = 0;
        groupDataIndexes[header].forEach((dataIndex) => {
          const cellData = row[dataIndex];
          if (cellData && typeof cellData === "object" && "total" in cellData) {
            sum += cellData.total || 0;
          }
        });
        // Round each per-stage cell to 2dp so the stage-view displayed cells,
        // column totals (`stageTotals`) and row totals (`stageRowTotal`) all
        // operate on the same 2dp value — mirrors the plot-view normalisation
        // in `applyPlotMatrixUplift`.
        const stageCell = parseFloat(sum.toFixed(2));
        newRow[`stage_${header}`] = stageCell;
        rowTotal += stageCell;
      });

      newRow.stageRowTotal = parseFloat(rowTotal.toFixed(2));
      return newRow;
    });

    // Inject separator between plot rows and garage rows (same logic as Plot View)
    const hasGarageRows = sDataRaw.some((r) => r.rowType === "garage");
    const sData: any[] = [];
    if (hasGarageRows) {
      let separatorInserted = false;
      sDataRaw.forEach((row) => {
        if (!separatorInserted && row.rowType === "garage") {
          sData.push({ key: "__garage_separator__", _isSeparator: true });
          separatorInserted = true;
        }
        sData.push(row);
      });
    } else {
      sData.push(...sDataRaw);
    }

    return {
      stageColumns: stageCols,
      stageData: sData,
      stageGroupKeys: groupOrder,
    };
  }, [dynamicColumns, displayPlotMatrixData]);

  // Save/restore scroll position when switching views
  const handleViewModeChange = (value: string | number) => {
    // Save current scroll position
    const tableBody = document.querySelector(
      ".plot-matrix-table .ant-table-body",
    );
    if (tableBody) {
      scrollPositionRef.current = {
        left: tableBody.scrollLeft,
        top: tableBody.scrollTop,
      };
    }
    setViewMode(value as "plot" | "stage");
    // Restore scroll position after render
    requestAnimationFrame(() => {
      const newTableBody = document.querySelector(
        ".plot-matrix-table .ant-table-body",
      );
      if (newTableBody) {
        newTableBody.scrollTop = scrollPositionRef.current.top;
      }
    });
  };

  // Inject a separator row between plot rows and garage rows for display purposes.
  // The separator is not a real data row — it carries a sentinel key and is rendered
  // as a full-width label via rowClassName + custom cell render.
  const plotMatrixDataWithSeparator = useMemo(() => {
    const hasGarageRows = displayPlotMatrixData.some((r) => r.rowType === 'garage');
    if (!hasGarageRows) return displayPlotMatrixData;

    const result: any[] = [];
    let separatorInserted = false;
    displayPlotMatrixData.forEach((row) => {
      if (!separatorInserted && row.rowType === 'garage') {
        result.push({ key: '__garage_separator__', _isSeparator: true });
        separatorInserted = true;
      }
      result.push(row);
    });
    return result;
  }, [displayPlotMatrixData]);

  // Determine active columns/data based on view mode
  const activeColumns = (viewMode === "plot" ? columns : stageColumns) as ColumnsType<PlotMatrixRow>;
  const activeData = viewMode === "plot" ? plotMatrixDataWithSeparator : stageData;

  // Build export-ready columns and data for the current view mode
  const getExportPayload = () => {
    if (viewMode === "stage") {
      const cols = stageGroupKeys.map((header) => ({
        key: `stage_${header}`,
        stepName: header,
        categoryName: header,
        fieldNames: [],
        hasData: true,
        dataIndex: `stage_${header}`,
      }));
      // Map stage rows preserving rowType so the Excel exporter can insert the
      // GARAGES separator. Separator sentinel rows are passed through unchanged.
      const rows = stageData.map((row) => {
        if (row._isSeparator) return row;
        const exportRow: any = {
          key: row.key,
          plotNo: formatPlotNoWithPrefix(row.plotNo, row.rowType),
          housetype: row.housetype,
          rowType: row.rowType,
        };
        stageGroupKeys.forEach((header) => {
          exportRow[`stage_${header}`] = { total: row[`stage_${header}`] || 0 };
        });
        return exportRow;
      });
      return { columns: cols, rows };
    }
    // Plot view — apply the P-/G- prefix to each row's plotNo so the export
    // matches what the matrix table displays. Source is the uplifted
    // `displayPlotMatrixData` so exported cells carry the prelims+OH&P
    // markup (matching what the user sees on screen).
    const prefixedRows = displayPlotMatrixData.map((row: any) => ({
      ...row,
      plotNo: formatPlotNoWithPrefix(row.plotNo, row.rowType),
    }));
    return { columns: dynamicColumns, rows: prefixedRows };
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    if (!plotMatrixData || plotMatrixData.length === 0) {
      message.warning("No plot data available to export");
      return;
    }

    const { columns: expCols, rows: expRows } = getExportPayload();

    setIsExportingToExcel(true);
    try {
      // Resolve markup from the user's checkbox state so the exported Excel
      // matches the on-screen footer (uplifted cells). A disabled markup is
      // passed as 0. Discount was removed from Plot Matrix — always 0.
      const markup = {
        prelimsPct: includePreliminaries ? preliminariesPercent : 0,
        ohpPct: includeOhp ? ohpPercent : 0,
        discountPct: 0,
      };
      await exportPlotMatrixToExcel(
        state.groundworks,
        {
          projectName: state.projectName || "Plot Matrix",
          clientName: state.client || "CLIENT NAME",
          region: state.region || "REGION",
          siteAddress: state.siteAddress,
        },
        ratesMapping,
        itemLibraryData,
        expCols,
        expRows,
        viewMode,
        markup,
      );

      notification.success({
        message: "Export Successful",
        description: `Plot Matrix has been exported to Excel successfully with ${plotMatrixData.length} plots.`,
      });
    } catch (error) {
      notification.error({
        message: "Export Error",
        description: `Failed to export Plot Matrix: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
      console.error("Export error:", error);
    } finally {
      setIsExportingToExcel(false);
    }
  };

  // Shared summary row renderer for both embedded and standalone tables.
  // Renders the GRAND TOTAL row. Prelims and OH&P are NOT shown as separate
  // footer rows — they're baked into every cell via `upliftFactor` so each
  // column / row / grand total already reflects the markup. Discount was
  // removed from Plot Matrix; no DISCOUNT or TENDER TOTAL rows are rendered.
  //
  // Grand Total = `Σ column totals` = `Σ row totals` — derived directly from
  // the displayed cells inside each view's branch so the bottom-right cell
  // reconciles against the matrix to the penny (any per-cell rounding drift
  // accumulates into one consistent number that all three displays agree on,
  // rather than a separate canonical sum that disagrees with the column /
  // row totals). This is the user's stated preference: matrix cells are
  // authoritative; Sum Analysis's grand total may diverge slightly when
  // per-field 2dp rounding produces fractional residuals.
  const renderSummaryRow = (data: readonly any[]) => {
    const formatCurrency = (val: number) =>
      `£${val.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Exclude the separator row from all summary calculations
    const summaryData = data.filter((r: any) => !r._isSeparator);

    if (viewMode === "stage") {
      // Valuation Stage view: sum plain number fields per stage column.
      // Cells in `stageData` already carry the uplift (derived from
      // `displayPlotMatrixData`), so summing them here gives uplifted
      // per-stage column totals automatically.
      const stageTotals: { [key: string]: number } = {};
      stageGroupKeys.forEach((header) => {
        const key = `stage_${header}`;
        let sum = 0;
        summaryData.forEach((record: any) => {
          sum += record[key] || 0;
        });
        // Round each column total to 2dp so summing many 2dp-rounded cells
        // doesn't accumulate sub-penny float residuals. Paired with the same
        // round on `calculateRowTotal` and on `applyPlotMatrixUplift` cell
        // values, the displayed cells, column-total strip, row-total column
        // and grand-total cell are all penny-consistent.
        stageTotals[key] = parseFloat(sum.toFixed(2));
      });

      // Grand total = sum of the displayed stage-column totals = sum of the
      // displayed `stageRowTotal` row totals (mathematically identical), so
      // the bottom-right cell reconciles exactly with both the column-total
      // strip and the per-row Total column.
      const upliftedGrandTotal = parseFloat(
        Object.values(stageTotals).reduce((s, v) => s + v, 0).toFixed(2),
      );

      const stageLeafColCount = stageGroupKeys.length;

      return (
        <Table.Summary.Row
          style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}
        >
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>GRAND TOTAL</strong>
          </Table.Summary.Cell>
          {stageGroupKeys.map((header, index) => (
            <Table.Summary.Cell
              key={`stage_${header}`}
              index={2 + index}
              align="right"
            >
              <strong>{formatCurrency(stageTotals[`stage_${header}`] || 0)}</strong>
            </Table.Summary.Cell>
          ))}
          <Table.Summary.Cell index={2 + stageLeafColCount} align="right">
            <strong style={{ fontSize: "16px" }}>
              {formatCurrency(upliftedGrandTotal)}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      );
    }

    // Plot view: sum {total} objects per sub-column. Cells are uplifted
    // (sourced from `displayPlotMatrixData` via `plotMatrixDataWithSeparator`)
    // so per-column totals carry the markup automatically.
    const columnTotals: { [key: string]: number } = {};
    dynamicColumns.forEach((col) => {
      let totalSum = 0;
      summaryData.forEach((record: any) => {
        const cellData = record[col.dataIndex];
        if (cellData) {
          totalSum += cellData.total || 0;
        }
      });
      // 2dp round — same rationale as the stage-view branch above.
      columnTotals[col.dataIndex] = parseFloat(totalSum.toFixed(2));
    });

    // Grand total = sum of the displayed column totals = sum of the per-row
    // `calculateRowTotal(record)` totals (mathematically identical), so the
    // bottom-right cell reconciles exactly with both the column-total strip
    // and the per-row Total column.
    const upliftedGrandTotal = parseFloat(
      Object.values(columnTotals).reduce((s, v) => s + v, 0).toFixed(2),
    );

    const plotLeafColCount = dynamicColumns.length;

    return (
      <Table.Summary.Row
        style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}
      >
        <Table.Summary.Cell index={0} colSpan={2}>
          <strong>GRAND TOTAL</strong>
        </Table.Summary.Cell>
        {dynamicColumns.map((col, index) => (
          <Table.Summary.Cell
            key={col.dataIndex}
            index={2 + index}
            align="right"
          >
            <strong>{formatCurrency(columnTotals[col.dataIndex] || 0)}</strong>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={2 + plotLeafColCount} align="right">
          <strong style={{ fontSize: "16px" }}>
            {formatCurrency(upliftedGrandTotal)}
          </strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const content = (
    <div className="max-w-full mx-auto">
      {!embedded && (
        <div
          className="flex justify-between items-center mb-2 mt-4"
          style={{
            position: "sticky",
            top: -5,
            zIndex: 20,
            backgroundColor: "white",
            paddingBottom: "16px",
          }}
        >
          <Title className="mb-0 text-blue-900 !text-2xl ">Plot Matrix</Title>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {/* Hiding Plot View/Valuation Stage View for now. It will be enabled later */}
            {/* <Segmented
              value={viewMode}
              onChange={handleViewModeChange}
              options={[
                { label: "Plot View", value: "plot" },
                { label: "Valuation Stage View", value: "stage" },
              ]}
              size="large"
            /> */}
            {renderCellCalcStrategySwitcher()}
            {renderMarkupCheckboxes()}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              disabled={!plotMatrixData || plotMatrixData.length === 0}
              loading={isExportingToExcel}
              size="large"
            >
              {isExportingToExcel ? "Exporting…" : "Export to Excel"}
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              type="default"
            >
              Back
            </Button>
          </div>
        </div>
      )}

      {embedded ? (
        <div style={{ padding: "16px" }}>
          {/* Header */}
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Title
              level={5}
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: "16px",
                color: "#111827",
              }}
            >
              Plot Matrix
            </Title>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <Segmented
                value={viewMode}
                onChange={handleViewModeChange}
                options={[
                  { label: "Plot View", value: "plot" },
                  { label: "Valuation Stage View", value: "stage" },
                ]}
              />
              {renderCellCalcStrategySwitcher()}
              {renderMarkupCheckboxes()}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                disabled={!plotMatrixData || plotMatrixData.length === 0}
                loading={isExportingToExcel}
              >
                {isExportingToExcel ? "Exporting…" : "Export to Excel"}
              </Button>
            </div>
          </div>

          {/* Loading overlay while item library fetches or matrix computes */}
          {(!isReady || isLoadingItemLibrary) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "80px 0",
                gap: "16px",
              }}
            >
              <Spin size="large" />
              <span style={{ color: "#6B7280", fontSize: "14px" }}>
                {isLoadingItemLibrary ? "Loading rate library…" : "Building plot matrix…"}
              </span>
            </div>
          )}

          <style>
            {`
              .plot-matrix-table .ant-table {
                border-radius: 0 !important;
              }
              .plot-matrix-table .ant-table-container {
                border-radius: 0 !important;
              }
              .plot-matrix-row-garage {
                background-color: #f0f4ff !important;
              }
              .plot-matrix-row-garage:hover > td {
                background-color: #e5ecff !important;
              }
              .plot-matrix-row-separator > td {
                background-color: #d1d5db !important;
                padding-top: 6px !important;
                padding-bottom: 6px !important;
              }
            `}
          </style>
          {isReady && !isLoadingItemLibrary && (
            <Table
              className="plot-matrix-table"
              dataSource={activeData}
              columns={activeColumns}
              scroll={{ x: "max-content" }}
              size="middle"
              bordered
              rowKey={(record) => record.key || record.plotNo}
              rowClassName={(record) => {
                if (record._isSeparator) return "plot-matrix-row-separator";
                if (record.rowType === "garage") return "plot-matrix-row-garage";
                return "";
              }}
              sticky={{ offsetHeader: 80 }}
              pagination={false}
              summary={(data) => renderSummaryRow(data)}
            />
          )}
        </div>
      ) : (
        <Card style={{ position: "sticky", top: "80px", zIndex: 10 }}>
          <Table
            className="plot-matrix-table"
            dataSource={activeData}
            columns={activeColumns}
            scroll={{ x: "max-content" }}
            size="small"
            bordered
            rowKey={(record) => record.key || record.plotNo}
            rowClassName={(record) => {
              if (record._isSeparator) return "plot-matrix-row-separator";
              if (record.rowType === "garage") return "plot-matrix-row-garage";
              return "";
            }}
            sticky={{ offsetHeader: 80 }}
            pagination={false}
            summary={(data) => renderSummaryRow(data)}
          />
        </Card>
      )}
    </div>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <MainLayout canProceed={true} showStepper={false}>
          {content}
        </MainLayout>
      )}

      {showDevMode && (
        <Modal
          open={formulaModal.visible}
          title={
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              Formula Inspector — {formulaModal.categoryName}
            </span>
          }
          onCancel={() => setFormulaModal((s) => ({ ...s, visible: false }))}
          footer={null}
          width={780}
          destroyOnClose
        >
          {formulaModal.visible &&
            getFormulaModalContent(
              formulaModal.categoryName,
              formulaModal.fieldNames,
              groundworksWithWallDimensions ?? state.groundworks,
              formulaInspectorRateMap,
            )}
        </Modal>
      )}

      {/* Dev-mode per-cell breakdown modal */}
      {showDevMode && cellBreakdownModal?.visible && (() => {
        const { breakdown, cellTotal, columnName, plotNo } = cellBreakdownModal;
        const fmt = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
        const fmt2 = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const pct = (n: number, d: number) => d === 0 ? '—' : `${((n / d) * 100).toFixed(2)}%`;

        const getDbRate = (resourceCode: string): number | undefined => {
          if (!resourceCode) return undefined;
          if (resourceCode.includes('+')) {
            return resourceCode.split('+').reduce((s, c) => s + (formulaInspectorRateMap.get(c.trim().toUpperCase()) || 0), 0);
          }
          return formulaInspectorRateMap.get(resourceCode.toUpperCase());
        };

        const strategyLabel: Record<string, string> = {
          proportional: 'Option A — Proportional (rowQty / totalQty × BoQ Amount)',
          rawRowQty: 'Option B — Raw Row Qty (rowQty × modifiedRate)',
          roundedRowQty: 'Option C — Rounded Row Qty (round(rowQty) × modifiedRate)',
        };

        // Break the uplift factor into its components for the footer rows
        const activePrelimPct = includePreliminaries && preliminariesPercent > 0 ? preliminariesPercent : 0;
        const activeOhpPct = includeOhp && ohpPercent > 0 ? ohpPercent : 0;
        const prelimAmount = breakdown.preUpliftTotal * (activePrelimPct / 100);
        const ohpAmount = breakdown.preUpliftTotal * (activeOhpPct / 100);

        return (
          <Modal
            open
            title={<span style={{ fontWeight: 700, fontSize: 14 }}>Cell Breakdown — {columnName} | Plot {plotNo}</span>}
            onCancel={() => setCellBreakdownModal(null)}
            footer={null}
            width={900}
            destroyOnClose
          >
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              Strategy: <strong style={{ color: '#374151' }}>{strategyLabel[breakdown.strategy] ?? breakdown.strategy}</strong>
            </div>

            {(() => {
              // Detect grouped build-up fields (fieldName contains ' — ')
              const hasGroups = breakdown.fields.some((f) => f.fieldName.includes(' — '));

              // Inline table renderer shared by flat view and each accordion panel
              const renderFieldRows = (fieldRows: typeof breakdown.fields) =>
                fieldRows.map((f, i) => {
                  const dbRate = getDbRate(f.resourceCode);
                  // For wall build-up items f.net is set (qty per metre of wall).
                  // rowQty = rowMetres × net, so to expose the net in the multiplier
                  // we divide by rowMetres (= rowQty / net) rather than rowQty;
                  // this yields  net × (modifiedRate / dbRate) × upliftFactor.
                  const effectiveQty = (f.net != null && f.net > 0)
                    ? f.rowQty / f.net   // rowMetres
                    : f.rowQty;
                  const multiplier = dbRate !== undefined && dbRate > 0 && effectiveQty !== 0
                    ? (f.contribution * upliftFactor) / (dbRate * effectiveQty)
                    : undefined;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', fontFamily: 'monospace', fontSize: 11 }}>
                        {hasGroups && f.fieldName.includes(' — ')
                          ? f.fieldName.substring(f.fieldName.indexOf(' — ') + 3)
                          : f.fieldName}
                      </td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{f.resourceCode || '—'}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{f.rowQty.toFixed(4)}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{f.totalQty.toFixed(4)}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{pct(f.rowQty, f.totalQty)}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{fmt2(f.billSummaryAmount)}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: dbRate !== undefined ? '#111827' : '#9ca3af' }}>
                        {dbRate !== undefined ? fmt(dbRate) : '—'}
                      </td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{fmt(f.modifiedRate)}</td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: multiplier !== undefined ? (Math.abs(multiplier - 1) > 0.001 ? '#d97706' : '#111827') : '#9ca3af' }}>
                        {multiplier !== undefined ? multiplier.toFixed(4) : '—'}
                      </td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{fmt2(f.contribution)}</td>
                    </tr>
                  );
                });

              const theadRow = (
                <tr style={{ background: '#f3f4f6' }}>
                  {['Field', 'Resource Code', 'Qty', 'Total Qty', 'Share', 'BoQ Amount', 'DB Rate', 'Modified Rate', 'Multiplier', 'Contribution'].map((h) => (
                    <th key={h} style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h === 'Field' || h === 'Resource Code' ? <span style={{ textAlign: 'left', display: 'block' }}>{h}</span> : h}
                    </th>
                  ))}
                </tr>
              );

              if (!hasGroups) {
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>{theadRow}</thead>
                    <tbody>{renderFieldRows(breakdown.fields)}</tbody>
                  </table>
                );
              }

              // Group fields: fields without ' — ' go into '_direct'; others group by prefix
              const groupMap = new Map<string, typeof breakdown.fields>();
              breakdown.fields.forEach((f) => {
                const sep = f.fieldName.indexOf(' — ');
                const key = sep >= 0 ? f.fieldName.substring(0, sep) : '_direct';
                groupMap.set(key, [...(groupMap.get(key) || []), f]);
              });

              const directFields = groupMap.get('_direct') || [];
              const specGroups = Array.from(groupMap.entries()).filter(([k]) => k !== '_direct');

              return (
                <div>
                  {/* Part 1 direct-rate fields shown as a plain table above the accordion */}
                  {directFields.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
                      <thead>{theadRow}</thead>
                      <tbody>{renderFieldRows(directFields)}</tbody>
                    </table>
                  )}

                  {/* Part 2 wall spec build-up as accordion */}
                  <Collapse
                    size="small"
                    style={{ fontSize: 12 }}
                    items={specGroups.map(([specId, specFields]) => {
                      const specContrib = specFields.reduce((s, f) => s + f.contribution, 0);
                      return {
                        key: specId,
                        label: (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{specId}</span>
                            <span style={{ color: '#374151' }}>{fmt2(specContrib)}</span>
                          </div>
                        ),
                        children: (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>{theadRow}</thead>
                            <tbody>{renderFieldRows(specFields)}</tbody>
                          </table>
                        ),
                      };
                    })}
                  />
                </div>
              );
            })()}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4 }}>
              <tfoot>
                <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                  <td colSpan={9} style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>Pre-uplift total</td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>{fmt2(breakdown.preUpliftTotal)}</td>
                </tr>
                {activePrelimPct > 0 && (
                  <tr style={{ background: '#f0fdf4', fontWeight: 500 }}>
                    <td colSpan={9} style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: '#16a34a' }}>
                      + Prelims ({activePrelimPct.toFixed(2)}%)
                    </td>
                    <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: '#16a34a' }}>{fmt2(prelimAmount)}</td>
                  </tr>
                )}
                {activeOhpPct > 0 && (
                  <tr style={{ background: '#fff7ed', fontWeight: 500 }}>
                    <td colSpan={9} style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: '#d97706' }}>
                      + OH&P ({activeOhpPct.toFixed(2)}%)
                    </td>
                    <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: '#d97706' }}>{fmt2(ohpAmount)}</td>
                  </tr>
                )}
                <tr style={{ background: '#eff6ff', fontWeight: 700 }}>
                  <td colSpan={9} style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right' }}>
                    Cell total
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '4px 6px', textAlign: 'right', color: '#1d4ed8' }}>{fmt2(cellTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </Modal>
        );
      })()}

      {/* Dev-mode cell-calc strategy info modal (standalone toolbar only).
          Same content as the modal in `GroundworksReview.tsx` — both render
          sites resolve their option from the shared `CELL_CALC_STRATEGY_OPTIONS`
          array so the descriptions stay in sync. */}
      {!isStrategyControlledByParent && (
        <Modal
          title={
            cellCalcInfoIdx !== null
              ? CELL_CALC_STRATEGY_OPTIONS[cellCalcInfoIdx].label
              : ""
          }
          open={cellCalcInfoIdx !== null}
          onCancel={() => setCellCalcInfoIdx(null)}
          footer={[
            <Button key="close" onClick={() => setCellCalcInfoIdx(null)}>
              Close
            </Button>,
          ]}
          width={620}
        >
          {cellCalcInfoIdx !== null && (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 13,
                lineHeight: 1.5,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                padding: 12,
                margin: 0,
              }}
            >
              {CELL_CALC_STRATEGY_OPTIONS[cellCalcInfoIdx].info}
            </pre>
          )}
        </Modal>
      )}

    </>
  );
};

export default PlotMatrix;
